import { Box, Dialog, Stack, Typography } from '@mui/material';
import React, { useId } from 'react';

import {
  Button,
  ButtonColorProps,
  ButtonSizeProps,
  ButtonVariantProps,
} from '../../../../../components/atoms/Button';

/**
 * The confirmation step for the Share & Schedule dialog.
 *
 * Not `molecules/ConfirmationPopup`: that builds on the `Modal` atom, which
 * hardcodes `disablePortal`. Rendered inside another modal, MUI's modal manager
 * then applies `aria-hidden` to the subtree the popup itself lives in, so its
 * heading and buttons are invisible to assistive tech — a real defect, proven by
 * `getByRole` finding no accessible roles at all in the confirm test. The atom
 * has 34 consumers, so the fix is a page-local `Dialog` instead.
 */
export interface ReportEditConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** Colours the confirm button as a destructive action (e.g. removing access). */
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const PAPER_SX = {
  width: 'min(400px, calc(100vw - 48px))',
  maxWidth: 'none',
  borderRadius: 2,
  bgcolor: 'v4.surface',
  p: 2.5,
};

export const ReportEditConfirmDialog: React.FC<ReportEditConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  onCancel,
  onConfirm,
}) => {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      slotProps={{ paper: { sx: PAPER_SX } }}
    >
      <Box>
        <Typography id={titleId} variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography id={descriptionId} variant="body2" sx={{ mt: 0.5, color: 'v4.textMuted' }}>
          {description}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2.5 }}>
          <Button
            variant={ButtonVariantProps.Secondary}
            size={ButtonSizeProps.Small}
            color={ButtonColorProps.Secondary}
            sx={{ textTransform: 'none' }}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={ButtonVariantProps.Primary}
            size={ButtonSizeProps.Small}
            color={destructive ? ButtonColorProps.Error : ButtonColorProps.Primary}
            sx={{ textTransform: 'none' }}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};
